import { useEffect, useState } from "react";
import { api, formatDateTime, formatFileSize } from "@/lib/api";
import { Download, Trash2, FileText, FileImage, FileType, Loader2, Eye, EyeOff, Pencil, PenTool } from "lucide-react";
import { toast } from "sonner";
import { getAttachmentOpenUrl } from "@/lib/attachmentDownload";

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * Visual list item for an attachment.
 * Shows image thumbnail for images, PDF icon for PDFs, plus description and actions.
 * Click sulla miniatura/nome file = anteprima in nuova scheda.
 * Click sulla matita = rinomina file.
 *
 * Props:
 *  - showClientVisibilityToggle: bool — se true, abilita il toggle "Visibile al cliente"
 *    (usabile solo per allegati polizza). Default `false`.
 *  - onRequestSignature: (att) => void  — se passata, mostra il pulsante "Invia per Firma OTP".
 *    Il chiamante gestisce il flusso (dialog cliente + POST /api/attachments/request-signature).
 */
export function AttachmentItem({ att, onDownload, onDelete, onPreview, onRename, showClientVisibilityToggle = false, onRequestSignature = null }) {
  const [thumb, setThumb] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(att.original_filename);
  const [clientVisible, setClientVisible] = useState(!!att.client_visible);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  useEffect(() => { setName(att.original_filename); }, [att.original_filename]);
  useEffect(() => { setClientVisible(!!att.client_visible); }, [att.client_visible]);
  const isImage = IMAGE_TYPES.has(att.content_type);
  const isPdf = att.content_type === "application/pdf";
  const canPreview = isImage || isPdf;

  const toggleClientVisibility = async () => {
    setTogglingVisibility(true);
    const next = !clientVisible;
    try {
      await api.patch(`/attachments/${att.id}/client-visibility`, { client_visible: next });
      setClientVisible(next);
      toast.success(next ? "Allegato visibile al cliente" : "Allegato nascosto al cliente");
    } catch (e) { toast.error("Errore aggiornamento visibilità"); }
    finally { setTogglingVisibility(false); }
  };

  const renameFile = async () => {
    const current = name || "";
    const dotIdx = current.lastIndexOf(".");
    const ext = dotIdx >= 0 ? current.slice(dotIdx) : "";
    const base = dotIdx >= 0 ? current.slice(0, dotIdx) : current;
    const input = window.prompt("Nuovo nome del file (l'estensione viene preservata):", base);
    if (input === null) return;
    const cleaned = input.trim();
    if (!cleaned) { toast.error("Il nome non può essere vuoto"); return; }
    let newName = cleaned;
    if (ext && !newName.toLowerCase().endsWith(ext.toLowerCase())) newName = newName + ext;
    if (newName === current) return;
    try {
      const { data } = await api.patch(`/attachments/${att.id}/rename`, { filename: newName });
      const finalName = data?.filename || newName;
      setName(finalName);
      onRename?.(att, finalName);
      toast.success("File rinominato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la rinomina");
    }
  };

  useEffect(() => {
    let revoke = null;
    if (isImage) {
      setLoading(true);
      api.get(`/attachments/${att.id}/download`, { responseType: "blob" })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          revoke = url;
          setThumb(url);
          setPreviewUrl(url);
        })
        .catch(() => setThumb(null))
        .finally(() => setLoading(false));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [att.id, isImage]);

  const openPreview = async () => {
    // Se il parent fornisce un handler (es. lightbox), usalo
    if (onPreview) { onPreview(att); return; }
    if (!canPreview) {
      onDownload(att);
      return;
    }
    try {
      // Prova prima signed URL GCS (bypass backend); fallback blob per file legacy
      const { url } = await getAttachmentOpenUrl(att.id, { contentType: att.content_type });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      onDownload(att);
    }
  };

  return (
    <div
      data-testid={`attachment-item-${att.id}`}
      className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md p-3 hover:border-blue-300 transition-colors"
    >
      {/* Visual preview (cliccabile) */}
      <button
        type="button"
        data-testid={`preview-${att.id}`}
        onClick={openPreview}
        title={canPreview ? "Apri anteprima in nuova scheda" : "Scarica"}
        className="h-14 w-14 flex-shrink-0 rounded-md border border-slate-200 bg-white overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-blue-400 transition-shadow cursor-pointer relative group"
      >
        {isImage && thumb && (
          <img src={thumb} alt={name} className="h-full w-full object-cover" data-testid={`thumb-${att.id}`} />
        )}
        {isImage && !thumb && loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        {isImage && !thumb && !loading && <FileImage className="h-6 w-6 text-slate-400" />}
        {isPdf && <FileText className="h-7 w-7 text-red-500" />}
        {!isImage && !isPdf && <FileType className="h-6 w-6 text-slate-400" />}
        {canPreview && (
          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
            <Eye className="h-5 w-5 text-white" />
          </span>
        )}
      </button>

      {/* Meta + description */}
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={openPreview}
          data-testid={`preview-name-${att.id}`}
          className="font-medium text-sm truncate text-left hover:text-blue-600 hover:underline w-full"
          title={canPreview ? "Apri anteprima in nuova scheda" : "Scarica"}
        >
          {name}
        </button>
        {att.category && (
          <span
            data-testid={`category-${att.id}`}
            className="inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 border border-blue-200 dark:border-blue-700"
            title="Categoria allegato"
          >
            {att.category}
          </span>
        )}
        {att.description && (
          <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5" data-testid={`desc-${att.id}`}>{att.description}</div>
        )}
        <div className="text-xs text-slate-500 mt-0.5">
          {formatFileSize(att.size)} · {(att.content_type || "").split("/")[1]?.toUpperCase() || "FILE"}
          {att.kind && ` · ${att.kind.replace("_", " ")}`}
          {att.created_at && (
            <span data-testid={`uploaded-at-${att.id}`} className="ml-1.5 text-slate-400" title="Data e ora di caricamento">
              · 🕒 {formatDateTime(att.created_at)}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 ml-2">
        {showClientVisibilityToggle && (
          <button
            data-testid={`toggle-client-visible-${att.id}`}
            onClick={toggleClientVisibility}
            disabled={togglingVisibility}
            className={`p-2 rounded transition-colors ${
              clientVisible
                ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-200"
                : "hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            }`}
            title={clientVisible
              ? "Visibile al cliente nell'area privata · clicca per nascondere"
              : "Nascosto al cliente · clicca per renderlo visibile nell'area privata"}
          >
            {togglingVisibility
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : clientVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />
            }
          </button>
        )}
        <button
          data-testid={`rename-attach-${att.id}`}
          onClick={renameFile}
          className="p-2 rounded hover:bg-amber-50 text-amber-600 hover:text-amber-800"
          title="Rinomina file"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          data-testid={`download-attach-${att.id}`}
          onClick={() => onDownload(att)}
          className="p-2 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800"
          title="Scarica"
        >
          <Download className="h-4 w-4" />
        </button>
        {onRequestSignature && (
          <button
            data-testid={`sign-attach-${att.id}`}
            onClick={() => onRequestSignature(att)}
            className="p-2 rounded hover:bg-indigo-50 text-indigo-600 hover:text-indigo-800"
            title="Invia per Firma OTP"
          >
            <PenTool className="h-4 w-4" />
          </button>
        )}
        <button
          data-testid={`delete-attach-${att.id}`}
          onClick={() => onDelete(att)}
          className="p-2 rounded hover:bg-red-50 text-red-600 hover:text-red-800"
          title="Elimina"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
